import React, { useEffect, useMemo, useState } from 'react';
import { getMemoryStats, getCurrentFolder, setCurrentFolder } from './localStorage';
import { getDb } from './firebase';
import { collection, onSnapshot, doc, getDoc, setDoc, writeBatch, getCountFromServer, updateDoc } from 'firebase/firestore';

// Sets without a folder field are shown under this name
const DEFAULT_FOLDER = 'Other';
const folderOf = (option) => (option.folder || '').trim() || DEFAULT_FOLDER;

// Example file for the "Download template" button: one entry per word shape the app supports
const TEMPLATE_WORDS = [
  {
    id: 1,
    swedish: 'exempelord',
    pronunciation: 'ek-sem-pel-oord',
    english: 'example word (noun)',
    type: 'noun',
    article: 'ett',
    plural: 'exempelord',
    example: {
      swedish: 'Det här är ett exempelord.',
      english: 'This is an example word.'
    }
  },
  {
    id: 2,
    swedish: {
      infinitive: 'testa',
      imperative: 'testa',
      present: 'testar',
      preteritum: 'testade',
      supinum: 'testat'
    },
    pronunciation: 'tes-ta',
    english: 'to test (verb)',
    type: 'verb'
  },
  {
    id: 3,
    swedish: 'påhittad',
    pronunciation: 'poh-hit-ad',
    english: 'made-up (adjective)',
    type: 'adjective'
  },
  {
    id: 4,
    swedish: 'hej då',
    english: 'goodbye (only swedish and english are required)',
    type: 'phrase'
  }
];

const downloadTemplate = () => {
  const blob = new Blob([JSON.stringify(TEMPLATE_WORDS, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'vocab_template.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const VocabularyMenu = ({ onSelectVocabulary, isEditor, editMode }) => {
  const [vocabularyOptions, setVocabularyOptions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentFolder, setCurrentFolderState] = useState(() => getCurrentFolder());
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newFolder, setNewFolder] = useState(() => getCurrentFolder() || '');
  const [newFile, setNewFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const db = getDb();

  const openFolder = (folder) => {
    setCurrentFolderState(folder);
    setCurrentFolder(folder);
    setNewFolder(folder || '');
  };

  const folders = useMemo(() => {
    const byName = new Map();
    vocabularyOptions.forEach(option => {
      const name = folderOf(option);
      const entry = byName.get(name) || { name, setCount: 0, wordCount: 0 };
      entry.setCount += 1;
      entry.wordCount += option.wordCount || 0;
      byName.set(name, entry);
    });
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [vocabularyOptions]);

  // If the remembered folder no longer exists, fall back to the folder list
  useEffect(() => {
    if (isLoaded && currentFolder && !folders.some(f => f.name === currentFolder)) {
      openFolder(null);
    }
  }, [isLoaded, currentFolder, folders]);

  const setsInFolder = currentFolder
    ? vocabularyOptions.filter(option => folderOf(option) === currentFolder)
    : [];

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'vocabSets'), (snapshot) => {
      const baseItems = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      (async () => {
        const items = await Promise.all(baseItems.map(async (it) => {
          const missing = it.wordCount == null; // only treat null/undefined as missing
          if (missing && it.id) {
            try {
              const cnt = await getCountFromServer(collection(db, `vocabSets/${it.id}/words`));
              const count = cnt.data().count;
              if (isEditor) {
                try {
                  await updateDoc(doc(db, 'vocabSets', it.id), { wordCount: count });
                } catch (_) {}
              }
              return { ...it, wordCount: count };
            } catch (_) {
              return it;
            }
          }
          return it;
        }));
        items.sort((a, b) => ((a.title || a.id || '')).localeCompare(b.title || b.id || ''));
        setVocabularyOptions(items);
        setIsLoaded(true);
      })();
    });
    return () => unsub();
  }, [db, isEditor]);

  const slugify = (s) => {
    return (s || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 64);
  };

  const handleCreateSet = async () => {
    if (!db || !isEditor) return;
    if (!newTitle.trim()) { alert('Please enter a name'); return; }
    if (newDescription.length > 40) { alert('Description must be at most 40 characters'); return; }
    if (!newFile) { alert('Please attach a JSON file'); return; }

    setIsCreating(true);
    try {
      const text = await newFile.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) throw new Error('JSON must be an array of word objects');

      let setId = slugify(newTitle);
      if (!setId) setId = `set-${Date.now()}`;
      const setRef = doc(db, 'vocabSets', setId);
      const existing = await getDoc(setRef);
      if (existing.exists()) {
        setId = `${setId}-${Date.now().toString(36)}`;
      }

      await setDoc(doc(db, 'vocabSets', setId), {
        id: setId,
        title: newTitle.trim(),
        description: newDescription.trim(),
        folder: newFolder.trim() || DEFAULT_FOLDER,
        wordCount: data.length,
        createdAt: Date.now()
      }, { merge: true });

      // Chunk into batches of 450
      const wordsColPath = `vocabSets/${setId}/words`;
      let idx = 0;
      while (idx < data.length) {
        const batch = writeBatch(db);
        const chunk = data.slice(idx, idx + 450);
        chunk.forEach((w, i) => {
          const docId = (w && (w.id !== undefined && w.id !== null)) ? String(w.id) : undefined;
          const ref = docId ? doc(db, wordsColPath, docId) : doc(collection(db, wordsColPath));
          batch.set(ref, { ...w, order: idx + i });
        });
        await batch.commit();
        idx += chunk.length;
      }

      // Reset form
      setNewTitle('');
      setNewDescription('');
      setNewFile(null);
      alert('Vocabulary set created');
    } catch (e) {
      console.error('Create set failed:', e);
      alert(`Create set failed: ${e.message || e}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="vocabulary-menu">
      {currentFolder && (
        <button className="back-to-menu" onClick={() => openFolder(null)} title="Back to folders">
          ←
        </button>
      )}

      <div className="menu-header">
        <h1>{currentFolder || 'Flash Cards'}</h1>
        <p>{currentFolder ? 'Choose a vocabulary set to practice' : 'Choose a folder'}</p>
      </div>

      <div className="vocabulary-options">
        {!currentFolder && folders.map(folder => (
          <div
            key={folder.name}
            className="vocabulary-option"
            onClick={() => openFolder(folder.name)}
          >
            <div className="option-flag">📁</div>
            <div className="option-content">
              <h3>{folder.name}</h3>
              <div className="option-stats">
                <span className="word-count">{folder.setCount} {folder.setCount === 1 ? 'set' : 'sets'}</span>
                <span className="word-count">{folder.wordCount} words</span>
              </div>
            </div>
            <div className="option-arrow">→</div>
          </div>
        ))}
        {setsInFolder.map(option => {
          const memoryStats = getMemoryStats(option.id, option.wordCount || 0);
          return (
            <div 
              key={option.id}
              className="vocabulary-option"
              onClick={() => onSelectVocabulary(option)}
            >
              <div className="option-flag">🇸🇪</div>
              <div className="option-content">
                <h3>{option.title || option.id}</h3>
                <p className="option-description">{option.description || ''}</p>
                <div className="option-stats">
                  <span className="word-count">{(option.wordCount ?? 0)} words</span>
                  {memoryStats.memorizedCount > 0 && (
                    <span className="memory-progress" style={{ 
                      color: '#28a745', 
                      fontSize: '12px',
                      marginLeft: '8px'
                    }}>
                      {memoryStats.completionPercentage}% memorized
                    </span>
                  )}
                </div>
              </div>
              <div className="option-arrow">→</div>
            </div>
          );
        })}
      </div>

      {isEditor && editMode && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          border: '1px solid #cfd4da',
          borderRadius: '10px',
          background: '#ffffff',
          color: '#212529',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          marginBottom: '16px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '5px' }}>Create new vocabulary set</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', alignItems: 'center' }}>
            <label>Name</label>
            <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder="e.g. Rivstart Chapter 6" autoCorrect="off" autoCapitalize="off" autoComplete="off" spellCheck={false} inputMode="text" style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }} />
            <label>Description</label>
            <input type="text" value={newDescription} maxLength={40} onChange={(e) => setNewDescription(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder="Max 40 characters" autoCorrect="off" autoCapitalize="off" autoComplete="off" spellCheck={false} inputMode="text" style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }} />
            <div></div>
            <div style={{ fontSize: '12px', opacity: 0.75 }}>{newDescription.length}/40</div>
            <label>Folder</label>
            <input type="text" list="vocab-folder-names" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder={`e.g. Simona (empty = ${DEFAULT_FOLDER})`} autoCorrect="off" autoCapitalize="off" autoComplete="off" spellCheck={false} inputMode="text" style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #cfd4da', borderRadius: '8px', background: '#fff', color: '#212529' }} />
            <datalist id="vocab-folder-names">
              {folders.map(f => <option key={f.name} value={f.name} />)}
            </datalist>
            <label>JSON file</label>
            <input type="file" accept="application/json,.json" onChange={(e) => setNewFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ fontSize: '14px' }} />
            <div></div>
            <div>
              <button type="button" onClick={downloadTemplate} style={{ padding: '6px 12px', fontSize: '13px', border: '1px solid #0d6efd', borderRadius: '8px', background: '#fff', color: '#0d6efd', cursor: 'pointer' }}>Download template</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            <button disabled={isCreating || !db} onClick={handleCreateSet} style={{ flex: 1, padding: '12px 14px', fontSize: '14px', border: 'none', borderRadius: '8px', background: '#0d6efd', color: '#fff' }}>{isCreating ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      )}

      <div className="menu-footer">
        <p>{currentFolder ? 'Click on any vocabulary set to start learning!' : 'Click on a folder to see its vocabulary sets'}</p>
      </div>
    </div>
  );
};

export default VocabularyMenu;